/**
 * LeadsMap — Mapa choropleth do Brasil com origem geográfica dos leads.
 *
 * Pinta estados por volume de leads. Hover mostra tooltip com total + top cidades.
 * Usa react-simple-maps + geojson em /brazil-states.geojson.
 */

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import Icon from './icons.jsx'
import { buildCityIndex, cityKey } from '../lib/cityMatch.js'

const GEO_URL = '/brazil-states.geojson'

// Mapeia "name" do geojson (estado por extenso) para UF (2 letras).
// Exportado p/ reuso (ex.: converter região da Meta → UF antes do mapa).
export const NAME_TO_UF = {
  'Acre': 'AC',
  'Alagoas': 'AL',
  'Amapá': 'AP',
  'Amazonas': 'AM',
  'Bahia': 'BA',
  'Ceará': 'CE',
  'Distrito Federal': 'DF',
  'Espírito Santo': 'ES',
  'Goiás': 'GO',
  'Maranhão': 'MA',
  'Mato Grosso': 'MT',
  'Mato Grosso do Sul': 'MS',
  'Minas Gerais': 'MG',
  'Pará': 'PA',
  'Paraíba': 'PB',
  'Paraná': 'PR',
  'Pernambuco': 'PE',
  'Piauí': 'PI',
  'Rio de Janeiro': 'RJ',
  'Rio Grande do Norte': 'RN',
  'Rio Grande do Sul': 'RS',
  'Rondônia': 'RO',
  'Roraima': 'RR',
  'Santa Catarina': 'SC',
  'São Paulo': 'SP',
  'Sergipe': 'SE',
  'Tocantins': 'TO',
}

// Escala de 5 tons (azul → verde → amarelo → laranja → vermelho).
// Índice 0 = quase nenhum dado; índice 4 = hotspot.
const HEAT_SCALE = ['#3B82F6', '#22C55E', '#EAB308', '#F97316', '#EF4444']
// Escala do modo `zoomable`: azul claro → azul escuro, em faixas de potência de 10
// (1–9, 10–99, 100–999, 1 mil–9,9 mil, 10 mil–99 mil, 100 mil ou mais).
const BLUE_SCALE = ['#BFDBFE', '#93C5FD', '#60A5FA', '#3B82F6', '#1D4ED8', '#1E3A8A']
const BLUE_SCALE_LABELS = ['1', '10', '100', '1 mil', '10 mil', '100 mil']
const STATE_OUTLINE = '#60A5FA'

// Faixa logarítmica: valor < 10 → 0, < 100 → 1, ... ≥ 100 mil → 5.
function logColor(value) {
  const bucket = value >= 1 ? Math.min(BLUE_SCALE.length - 1, Math.floor(Math.log10(value))) : 0
  return BLUE_SCALE[bucket]
}
const EMPTY_FILL = '#e8e8ed'

// Dimensões do viewBox do mapa e limites do zoom (modo `zoomable`).
const MAP_W = 520
const MAP_H = 440
const MIN_ZOOM = 1
const MAX_ZOOM = 60
const ZOOM_STEP = 1.5
// Visão inicial: o Brasil ocupa ~74% da largura e ~77% da altura do viewBox, então
// o zoom ideal depende do formato do card (no máx. 1.2x), sempre centralizado.
const INITIAL_K = 1.2
const BRAZIL_W = 0.74 * MAP_W
const BRAZIL_H = 0.77 * MAP_H
const centeredView = (k) => ({ k, x: (MAP_W * (1 - k)) / 2, y: (MAP_H * (1 - k)) / 2 })
const INITIAL_VIEW = centeredView(INITIAL_K)
function fitView(box) {
  const k = Math.min(INITIAL_K, (0.9 * (box.x1 - box.x0)) / BRAZIL_W, (0.82 * (box.y1 - box.y0)) / BRAZIL_H)
  return centeredView(Math.max(MIN_ZOOM, k))
}

// O svg ocupa o card todo (viewBox centralizado, "meet"). Devolve a área
// visível do card em unidades do viewBox, que pode ser maior que o viewBox.
function getBox(el) {
  const r = el.getBoundingClientRect()
  const s = Math.min(r.width / MAP_W, r.height / MAP_H)
  const vw = r.width / s
  const vh = r.height / s
  return { x0: (MAP_W - vw) / 2, x1: (MAP_W + vw) / 2, y0: (MAP_H - vh) / 2, y1: (MAP_H + vh) / 2 }
}

// O mapa pode ser arrastado até qualquer ponta dele chegar ao centro da área
// visível (assim dá para centralizar o norte, o sul, o leste ou o oeste).
function clampAxis(pos, size, v0, v1) {
  const center = (v0 + v1) / 2
  return Math.min(center, Math.max(center - size, pos))
}

function clampView(v, box) {
  return {
    k: v.k,
    x: clampAxis(v.x, MAP_W * v.k, box.x0, box.x1),
    y: clampAxis(v.y, MAP_H * v.k, box.y0, box.y1),
  }
}

// Aplica zoom `k` mantendo o ponto (cx, cy) do viewBox fixo na tela.
function zoomView(v, k, cx, cy, box) {
  const nk = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, k))
  return clampView({ k: nk, x: cx - ((cx - v.x) * nk) / v.k, y: cy - ((cy - v.y) * nk) / v.k }, box)
}

function getColor(value, max, scale = HEAT_SCALE) {
  if (!value || max <= 0) return EMPTY_FILL
  const ratio = value / max
  // Quintis: 0–20%, 20–40%, 40–60%, 60–80%, 80–100%.
  // Math.ceil garante que qualquer valor > 0 cai pelo menos no bucket 0 (azul).
  const bucket = Math.min(4, Math.max(0, Math.ceil(ratio * 5) - 1))
  return scale[bucket]
}

// ─── Municípios (modo `zoomable`) ───────────────────────────────────────────
// A malha municipal do IBGE (/municipios/<UF>.json) é carregada para todos os
// estados e cada município é pintado pelo valor da cidade. Os dados trazem só o
// nome da cidade, então o casamento é por nome normalizado dentro do estado.
// Os estados ficam neutros; só os que têm dados ganham uma borda mais forte.
const ALL_UFS = Object.values(NAME_TO_UF)
// As bordas dos municípios só aparecem com zoom >= BORDER_ZOOM (CSS .show-borders):
// com ~5.600 polígonos, traçar todas as bordas no zoom baixo trava o arrasto.
const BORDER_ZOOM = 3
const MUNI_URL = (uf) => `/municipios/${uf}.json`

// Malhas já pedidas (uma requisição por UF por sessão da página).
const muniCache = new Map()
function loadMunicipalities(uf) {
  if (!muniCache.has(uf)) {
    muniCache.set(
      uf,
      fetch(MUNI_URL(uf))
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((data) => {
          if (!data) muniCache.delete(uf) // permite tentar de novo
          return data
        })
    )
  }
  return muniCache.get(uf)
}
const EMPTY_INDEX = new Map()
const OUTLINE_STYLE = { outline: 'none', pointerEvents: 'none' }
const GEO_STYLE = {
  default: { outline: 'none', transition: 'filter .15s' },
  hover: { outline: 'none', cursor: 'pointer', filter: 'brightness(1.15) saturate(1.2)' },
  pressed: { outline: 'none' },
}

// Estados (camada base). Memoizada: arrastar o mapa só muda o transform do <g>
// pai, sem redesenhar os polígonos. Com `zoomable`, os estados ficam neutros
// (a cor vem dos municípios).
const StateLayer = memo(function StateLayer({ byUF, max, zoomable, onHover }) {
  return (
    <Geographies geography={GEO_URL}>
      {({ geographies }) =>
        geographies.map((geo) => {
          const name = geo.properties.name
          const uf = NAME_TO_UF[name] || name
          const stateData = byUF[uf]
          const leads = stateData?.leads || 0
          return (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              fill={zoomable ? EMPTY_FILL : getColor(leads, max)}
              stroke="#ffffff"
              strokeWidth={0.8}
              vectorEffect={zoomable ? 'non-scaling-stroke' : undefined}
              onMouseEnter={() => onHover({ uf, name, leads, topCities: stateData?.topCities || [] })}
              onMouseLeave={() => onHover(null)}
              style={GEO_STYLE}
            />
          )
        })
      }
    </Geographies>
  )
})

// Borda dos estados que têm dados, por cima dos municípios.
const StateOutlineLayer = memo(function StateOutlineLayer({ byUF }) {
  return (
    <Geographies geography={GEO_URL}>
      {({ geographies }) =>
        geographies
          .filter((geo) => byUF[NAME_TO_UF[geo.properties.name] || geo.properties.name]?.leads)
          .map((geo) => (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              fill="none"
              stroke={STATE_OUTLINE}
              strokeWidth={0.8}
              vectorEffect="non-scaling-stroke"
              style={{ default: OUTLINE_STYLE, hover: OUTLINE_STYLE, pressed: OUTLINE_STYLE }}
            />
          ))
      }
    </Geographies>
  )
})

const MUNI_STYLE = {
  default: { outline: 'none' },
  hover: { outline: 'none', cursor: 'pointer', fill: 'rgba(59,130,246,.14)' },
  pressed: { outline: 'none' },
}
// Municípios de um estado: cada cidade com dados é pintada pelo valor; as demais
// ficam transparentes (só fronteira e área de hover).
const NO_SELECTION = {}
const MunicipalityLayer = memo(function MunicipalityLayer({ uf, geoData, cityIndex, stateTotal, selected, onHover }) {
  return (
    <Geographies geography={geoData}>
      {({ geographies }) =>
        geographies.map((geo) => {
          const name = geo.properties.name
          const leads = cityIndex.get(cityKey(name)) || 0
          return (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              className={selected[name] !== undefined ? `bw-muni is-selected sel-${selected[name]}` : 'bw-muni'}
              data-uf={uf}
              data-name={name}
              data-leads={leads}
              fill={leads ? logColor(leads) : 'transparent'}
              stroke="rgba(255,255,255,.7)"
              strokeWidth={0.4}
              vectorEffect="non-scaling-stroke"
              onMouseEnter={() => onHover({ uf, name, leads, topCities: [], stateTotal })}
              onMouseLeave={() => onHover(null)}
              style={leads ? GEO_STYLE : MUNI_STYLE}
            />
          )
        })
      }
    </Geographies>
  )
})

/**
 * @param {function} [props.onCityClick] - Chamado com { uf, name, leads } ao clicar numa cidade (sem arrastar).
 * @param {Array<{uf: string, name: string}>} [props.selectedCities] - Cidades destacadas (a ordem define a cor: classe sel-N).
 * @param {ReactNode} [props.children] - Sobreposição dentro da área do mapa (ex.: coluna de cards).
 * @param {boolean} [props.zoomable] - Habilita arrastar (botão esquerdo), zoom
 *   pela roda do mouse, botões de centralizar / + / − e a camada de municípios.
 *   Desligado por padrão.
 */
export default function LeadsMap({
  data = [], metricLabel = 'leads', meta = null, zoomable = false, children = null,
  onCityClick = null, selectedCities = null,
}) {
  const [hover, setHover] = useState(null)
  const [view, setView] = useState(INITIAL_VIEW)
  const [dragging, setDragging] = useState(false)
  const wrapRef = useRef(null)
  const svgRef = useRef(null)
  const dragRef = useRef(null)
  const tipRef = useRef(null)
  const pointerRef = useRef(null)
  const downRef = useRef(null)
  const zoomBy = (factor) => {
    const box = getBox(wrapRef.current)
    setView((v) => zoomView(v, v.k * factor, MAP_W / 2, MAP_H / 2, box))
  }
  const resetView = () => setView(fitView(getBox(wrapRef.current)))

  // Posição do ponteiro em unidades do viewBox + fator px → viewBox.
  const toMapUnits = (clientX, clientY) => {
    const r = svgRef.current.getBoundingClientRect()
    const s = Math.min(r.width / MAP_W, r.height / MAP_H)
    return {
      x: (clientX - r.left - (r.width - MAP_W * s) / 2) / s,
      y: (clientY - r.top - (r.height - MAP_H * s) / 2) / s,
      unit: 1 / s,
    }
  }

  // Zoom pela roda: listener nativo não-passivo p/ poder bloquear o scroll da
  // página enquanto o mouse está sobre o mapa.
  useEffect(() => {
    const el = wrapRef.current
    if (!zoomable || !el) return undefined
    const onWheel = (e) => {
      if (e.target.closest?.('.mc-rail, .mapa-drawer, .mapa-panel, .mapa-period')) return // esses painéis rolam sozinhos
      e.preventDefault()
      const { x, y } = toMapUnits(e.clientX, e.clientY)
      const factor = Math.exp(-e.deltaY * 0.0015)
      const box = getBox(el)
      setView((v) => zoomView(v, v.k * factor, x, y, box))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomable])

  // Ajusta a visão inicial ao formato do card (largo e baixo ou estreito e alto).
  useLayoutEffect(() => {
    if (zoomable && wrapRef.current) setView(fitView(getBox(wrapRef.current)))
  }, [zoomable])

  // Com `zoomable`, o card segue o cursor (sem re-render; só muda o transform).
  const placeTip = () => {
    const tip = tipRef.current
    const wrap = wrapRef.current
    const ptr = pointerRef.current
    if (!tip || !wrap || !ptr) return
    const r = wrap.getBoundingClientRect()
    const gap = 16
    let x = ptr.x - r.left + gap
    let y = ptr.y - r.top + gap
    if (x + tip.offsetWidth > r.width - 8) x = ptr.x - r.left - tip.offsetWidth - gap
    if (y + tip.offsetHeight > r.height - 8) y = ptr.y - r.top - tip.offsetHeight - gap
    tip.style.transform = `translate(${Math.max(8, x)}px, ${Math.max(8, y)}px)`
  }
  useLayoutEffect(() => {
    if (zoomable && hover) placeTip()
  }, [zoomable, hover])

  const onPointerDown = (e) => {
    pointerRef.current = { x: e.clientX, y: e.clientY }
    if (!zoomable || e.button !== 0 || e.target.closest('.bw-map-controls, .mc-rail, .mapa-drawer, .mapa-panel, .mapa-period')) return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { px: e.clientX, py: e.clientY }
    downRef.current = { x: e.clientX, y: e.clientY }
    setDragging(true)
    setHover(null)
  }
  const onPointerMove = (e) => {
    if (zoomable) {
      pointerRef.current = { x: e.clientX, y: e.clientY }
      placeTip()
    }
    const d = dragRef.current
    if (!d) return
    const { unit } = toMapUnits(e.clientX, e.clientY)
    const dx = (e.clientX - d.px) * unit
    const dy = (e.clientY - d.py) * unit
    dragRef.current = { px: e.clientX, py: e.clientY }
    const box = getBox(wrapRef.current)
    setView((v) => clampView({ ...v, x: v.x + dx, y: v.y + dy }, box))
  }
  const endDrag = () => {
    dragRef.current = null
    setDragging(false)
  }
  // Clique (sem arrastar) numa cidade: o ponteiro fica capturado pelo mapa, então a
  // cidade é achada pelo elemento sob o cursor.
  const onPointerUp = (e) => {
    pointerRef.current = { x: e.clientX, y: e.clientY }
    const down = downRef.current
    downRef.current = null
    endDrag()
    if (!onCityClick || !down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 5) return
    const el = document.elementFromPoint(e.clientX, e.clientY)
    const uf = el?.getAttribute?.('data-uf')
    if (uf) onCityClick({ uf, name: el.getAttribute('data-name'), leads: Number(el.getAttribute('data-leads')) || 0 })
  }

  const { byUF, max, total } = useMemo(() => {
    const idx = {}
    let m = 0
    let t = 0
    for (const r of data) {
      idx[r.state] = r
      if (r.leads > m) m = r.leads
      t += r.leads
    }
    return { byUF: idx, max: m, total: t }
  }, [data])

  // uf → { nome da cidade → posição na seleção } (a posição escolhe a cor da borda).
  const selectedByUf = useMemo(() => {
    const out = {}
    ;(selectedCities || []).forEach((c, i) => {
      out[c.uf] = { ...(out[c.uf] || {}), [c.name]: i }
    })
    return out
  }, [selectedCities])

  // Malhas municipais carregadas, uma requisição por UF (aparecem conforme chegam).
  const [muniData, setMuniData] = useState({})
  useEffect(() => {
    if (!zoomable) return undefined
    let alive = true
    for (const uf of ALL_UFS) {
      loadMunicipalities(uf).then((data) => {
        if (alive && data) setMuniData((m) => (m[uf] ? m : { ...m, [uf]: data }))
      })
    }
    return () => {
      alive = false
    }
  }, [zoomable])

  const cityIndexes = useMemo(
    () => Object.fromEntries(Object.keys(byUF).map((uf) => [uf, buildCityIndex(uf, byUF[uf])])),
    [byUF]
  )

  return (
    <div className="bw-panel bw-map-panel">
      <div className="bw-panel-head">
        <Icon n="link" s={14} />
        <span>Origem geográfica · {metricLabel}</span>
        <span className="bw-map-total">{total.toLocaleString('pt-BR')} {metricLabel}</span>
      </div>

      <div
        ref={wrapRef}
        className={`bw-map-wrap${zoomable ? ' is-zoomable' : ''}${dragging ? ' is-dragging' : ''}${view.k >= BORDER_ZOOM ? ' show-borders' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={endDrag}
      >
        <ComposableMap
          ref={svgRef}
          projection="geoMercator"
          projectionConfig={{ scale: 580, center: [-54, -14] }}
          width={MAP_W}
          height={MAP_H}
          style={
            zoomable
              ? { position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', overflow: 'visible' }
              : { width: '100%', height: 'auto', aspectRatio: '520 / 440', maxHeight: '85vh', display: 'block' }
          }
        >
          <g transform={zoomable ? `translate(${view.x} ${view.y}) scale(${view.k})` : undefined}>
          <StateLayer
            byUF={byUF}
            max={max}
            zoomable={zoomable}
            onHover={setHover}
          />
          <g>
            {Object.entries(muniData).map(([uf, geoData]) => (
              <MunicipalityLayer
                key={uf}
                uf={uf}
                geoData={geoData}
                cityIndex={cityIndexes[uf] || EMPTY_INDEX}
                stateTotal={byUF[uf]?.leads || 0}
                selected={selectedByUf[uf] || NO_SELECTION}
                onHover={setHover}
              />
            ))}
          </g>
          {zoomable && <StateOutlineLayer byUF={byUF} />}
          </g>
        </ComposableMap>

        {zoomable && (
          <div className="bw-map-controls" role="group" aria-label="Controles do mapa">
            <button type="button" onClick={() => zoomBy(ZOOM_STEP)} disabled={view.k >= MAX_ZOOM} aria-label="Aumentar zoom" title="Aumentar zoom">
              <Icon n="plus" s={14} />
            </button>
            <button type="button" onClick={() => zoomBy(1 / ZOOM_STEP)} disabled={view.k <= MIN_ZOOM} aria-label="Diminuir zoom" title="Diminuir zoom">
              <span aria-hidden="true">−</span>
            </button>
            <button type="button" onClick={resetView} aria-label="Centralizar mapa" title="Centralizar mapa">
              <Icon n="target" s={14} />
            </button>
          </div>
        )}

        {children}

        {hover && (
          <div className="bw-map-tooltip" ref={tipRef}>
            <div className="bw-map-tt-head">
              <span className="bw-map-tt-uf">{hover.uf}</span>
              <span className="bw-map-tt-name">{hover.name}</span>
            </div>
            <div className="bw-map-tt-leads">
              <strong>{hover.leads.toLocaleString('pt-BR')}</strong> {metricLabel}
            </div>
            {hover.stateTotal > 0 && (
              <div className="bw-map-tt-share">
                <span>
                  {(hover.leads / hover.stateTotal * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% de {hover.uf}
                </span>
                <span>
                  total {hover.uf}: {hover.stateTotal.toLocaleString('pt-BR')} {metricLabel}
                </span>
              </div>
            )}
            {hover.topCities.length > 0 && (
              <ul className="bw-map-tt-cities">
                {hover.topCities.map((c) => (
                  <li key={c.name}>
                    <span>{c.name}</span>
                    <span>{c.leads}</span>
                  </li>
                ))}
              </ul>
            )}
            {hover.leads === 0 && <div className="bw-map-tt-empty">sem {metricLabel} no período</div>}
          </div>
        )}
      </div>

      <div className="bw-map-legend">
        {zoomable ? (
          <span className="bw-map-scale is-log">
            {BLUE_SCALE.map((c, i) => (
              <span key={c} className="bw-map-step">
                <i style={{ background: c }} />
                <small>{BLUE_SCALE_LABELS[i]}</small>
              </span>
            ))}
          </span>
        ) : (
          <>
            <span>Menos</span>
            <span className="bw-map-scale">
              {HEAT_SCALE.map((c) => (
                <i key={c} style={{ background: c }} />
              ))}
            </span>
            <span>Mais</span>
          </>
        )}
      </div>

      {meta && meta.unplaced > 0 && (
        <p className="bw-map-note">
          <Icon n="alert" s={11} />
          <span>
            {meta.unplaced.toLocaleString('pt-BR')} de {meta.total.toLocaleString('pt-BR')} {metricLabel} sem
            georreferenciamento
            {(meta.noLocation > 0 || meta.unrecognized > 0) && (
              <>
                {' '}({meta.noLocation > 0 && `${meta.noLocation.toLocaleString('pt-BR')} sem localização`}
                {meta.noLocation > 0 && meta.unrecognized > 0 && ' · '}
                {meta.unrecognized > 0 && `${meta.unrecognized.toLocaleString('pt-BR')} fora do Brasil`})
              </>
            )}
            . Contam nos totais, não no mapa.
          </span>
        </p>
      )}
    </div>
  )
}
